import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useConvertEnquiry } from "../hooks/useEnquiries";
import type { EnquiryDetail } from "../types";

function splitName(full: string | null): { first: string; last: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function ConvertToContactModal({
  enquiry,
  open,
  onOpenChange,
}: {
  enquiry: EnquiryDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const navigate = useNavigate();
  const convert = useConvertEnquiry(enquiry.id);
  // API caps limit at 100; requesting more returns 400 and leaves the
  // dropdown empty (the silent-failure mode that bit v0.15.0).
  const users = useUserList({ limit: 100, status: "ACTIVE" });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  const [createCompany, setCreateCompany] = useState(false);
  const [companyType, setCompanyType] = useState<
    "Prospect" | "Customer" | "Vendor"
  >("Prospect");
  const [companyCountry, setCompanyCountry] = useState("");

  const [createTask, setCreateTask] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskAssigneeId, setTaskAssigneeId] = useState("");
  const [taskDueDate, setTaskDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const { first, last } = splitName(enquiry.name);
      setFirstName(first);
      setLastName(last);
      setEmail(enquiry.email ?? "");
      setPhone(enquiry.phone ?? "");
      setJobTitle(enquiry.jobTitle ?? "");
      setCreateCompany(!!enquiry.companyName);
      setCompanyType("Prospect");
      setCompanyCountry(enquiry.country ?? "");
      setCreateTask(true);
      setTaskTitle(`Follow up: ${enquiry.subject ?? enquiry.code}`);
      setTaskAssigneeId(enquiry.assignedToId ?? "");
      setTaskDueDate("");
      setError(null);
    }
  }, [open, enquiry]);

  async function handleConvert() {
    setError(null);
    if (!firstName.trim()) {
      setError("First name is required.");
      return;
    }
    try {
      const res = await convert.mutateAsync({
        contact: {
          firstName: firstName.trim(),
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          jobTitle: jobTitle.trim() || undefined,
        },
        createCompany,
        companyType: createCompany ? companyType : undefined,
        companyCountry: createCompany
          ? companyCountry.trim() || undefined
          : undefined,
        createTask,
        taskTitle: createTask ? taskTitle.trim() || undefined : undefined,
        taskAssigneeId: createTask ? taskAssigneeId || undefined : undefined,
        taskDueDate: createTask ? taskDueDate || undefined : undefined,
      });
      onOpenChange(false);
      navigate(`/contacts/${res.contactId}`);
    } catch (e) {
      setError((e as Error)?.message ?? "Conversion failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Convert to Contact</DialogTitle>
          <DialogDescription>
            Create a Contact from this enquiry, optionally with a Company and a
            follow-up Task.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground">
              Contact info
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First Name *</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                checked={createCompany}
                onChange={(e) => setCreateCompany(e.target.checked)}
              />
              Create Company
              {enquiry.companyName ? ` — “${enquiry.companyName}”` : ""}
            </label>
            {createCompany && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select
                    value={companyType}
                    onChange={(e) =>
                      setCompanyType(
                        e.target.value as "Prospect" | "Customer" | "Vendor",
                      )
                    }
                  >
                    <option value="Prospect">Prospect</option>
                    <option value="Customer">Customer</option>
                    <option value="Vendor">Vendor</option>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input
                    value={companyCountry}
                    onChange={(e) => setCompanyCountry(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                checked={createTask}
                onChange={(e) => setCreateTask(e.target.checked)}
              />
              Create follow-up Task
            </label>
            {createTask && (
              <div className="space-y-3 pl-6">
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Assignee</Label>
                    <Select
                      value={taskAssigneeId}
                      onChange={(e) => setTaskAssigneeId(e.target.value)}
                      disabled={users.isLoading}
                    >
                      <option value="">
                        {users.isLoading
                          ? "Loading users…"
                          : users.isError
                            ? "Failed to load users"
                            : (users.data?.data.length ?? 0) === 0
                              ? "No users available"
                              : "— Unassigned —"}
                      </option>
                      {users.data?.data.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </Select>
                    {users.isError && (
                      <p className="text-xs text-destructive">
                        {(users.error as Error)?.message ??
                          "Could not load users."}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConvert}
            disabled={convert.isPending}
          >
            {convert.isPending && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
            )}
            Convert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
